import React, { useState } from 'react';
import { X, Mail, Lock, User, Phone, Loader2, LogIn, UserPlus, ShieldCheck, KeyRound } from 'lucide-react';
import { useAuth } from '../context/AuthContext.jsx';
import { useLanguage } from '../context/LanguageContext.jsx';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthModal = ({ isOpen, onClose }) => {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifyMode, setVerifyMode] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSending, setForgotSending] = useState(false);
  const { login, signup, verifyEmail, resendVerification } = useAuth();
  const { t } = useLanguage();

  if (!isOpen) return null;

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return;
    setForgotSending(true);
    try {
      // Backend always returns 200 regardless of whether the email exists
      // (account-enumeration protection), so we just trust it.
      await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail.trim().toLowerCase() }),
      });
      toast.success('If that email is on file, a reset link has been sent.');
      setForgotMode(false);
      setForgotEmail('');
    } catch (err) {
      // Even network errors shouldn't reveal anything — show the same message
      toast.success('If that email is on file, a reset link has been sent.');
      setForgotMode(false);
    } finally {
      setForgotSending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'login') {
        const data = await login(email, password);
        toast.success('Welcome back!');
        if (data?.user && data.user.email_verified === false) {
          setVerifyMode(true); setLoading(false); return;
        }
      } else {
        // Signup — phone validation client-side
        const digits = (phone || '').replace(/\D/g, '');
        if (digits.length < 10) {
          toast.error('Please enter a valid phone (10+ digits)');
          setLoading(false); return;
        }
        await signup(email, password, name, 'en', phone);
        toast.success('Account created! Check your email for the verification code.');
        setVerifyMode(true);
        setLoading(false);
        return;
      }
      onClose();
      setEmail(''); setPassword(''); setName(''); setPhone('');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!verifyCode || verifyCode.length !== 6) {
      toast.error('Please enter the 6-digit code'); return;
    }
    setLoading(true);
    try {
      await verifyEmail(verifyCode);
      toast.success('Email verified!');
      onClose();
      setVerifyMode(false); setVerifyCode('');
      setEmail(''); setPassword(''); setName(''); setPhone('');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      await resendVerification();
      toast.success('New code sent — check your email');
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <div 
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px' }}
      onClick={onClose}
    >
      <div 
        data-clarity-mask="True"
        style={{ background: 'var(--bg-secondary)', borderRadius: '16px', width: '100%', maxWidth: '420px', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.5), 0 0 20px rgba(0, 229, 255, 0.08)', border: '1px solid var(--border-light)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg, #0A1F44 0%, #0F2E6D 100%)', padding: '24px', position: 'relative' }}>
          <button onClick={onClose} data-testid="auth-modal-close" style={{ position: 'absolute', top: '16px', right: '16px', background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '8px', padding: '6px', cursor: 'pointer' }}>
            <X size={18} color="white" />
          </button>
          <h2 style={{ color: 'white', fontSize: '1.5rem', fontWeight: '600', margin: 0 }}>
            {forgotMode ? 'Reset Password' : verifyMode ? 'Verify Your Email' : (mode === 'login' ? t('auth.welcomeBack') : t('auth.createAccount'))}
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', margin: '8px 0 0' }}>
            {forgotMode ? "We'll email you a link to set a new password" : verifyMode ? 'Enter the 6-digit code sent to your email' : (mode === 'login' ? t('auth.signInDesc') : t('auth.signUpDesc'))}
          </p>
        </div>

        {/* Forgot Password Form */}
        {forgotMode ? (
          <form onSubmit={handleForgotPassword} style={{ padding: '24px' }} data-testid="forgot-password-form">
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', color: 'var(--text-secondary)', fontWeight: '500' }}>{t('auth.email') || 'Email'}</label>
              <div style={{ position: 'relative' }}>
                <Mail size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-light)' }} />
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoFocus
                  autoComplete="email"
                  data-testid="forgot-password-email-input"
                  style={{ width: '100%', padding: '12px 12px 12px 42px', border: '1px solid var(--border-medium)', borderRadius: '8px', fontSize: '1rem', outline: 'none', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                />
              </div>
            </div>
            <button type="submit" disabled={forgotSending || !forgotEmail.trim()} data-testid="forgot-password-submit"
              style={{ width: '100%', padding: '14px', background: forgotSending ? '#94a3b8' : '#00E5FF', color: '#000', border: 'none', borderRadius: '10px', fontSize: '1rem', fontWeight: '600', cursor: forgotSending ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', opacity: forgotSending ? 0.7 : 1, boxShadow: '0 0 12px #00E5FF' }}>
              {forgotSending ? <Loader2 size={20} className="animate-spin" /> : <><KeyRound size={20} />Send Reset Link</>}
            </button>
            <p style={{ marginTop: '16px', textAlign: 'center', fontSize: '0.9rem' }}>
              <button type="button" onClick={() => { setForgotMode(false); setForgotEmail(''); }}
                style={{ background: 'none', border: 'none', color: '#00E5FF', cursor: 'pointer', textDecoration: 'underline' }}>
                ← Back to sign in
              </button>
            </p>
          </form>
        ) : (<>

        {/* Verification Form */}
        {verifyMode ? (
          <form onSubmit={handleVerify} style={{ padding: '24px' }} data-testid="email-verify-form">
            <div style={{ background: 'rgba(0, 229, 255, 0.05)', border: '1px solid rgba(0, 229, 255, 0.2)', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px' }}>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
                <ShieldCheck size={14} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle', color: '#00E5FF' }} />
                We sent a 6-digit code to <strong style={{ color: 'var(--text-primary)' }}>{email || 'your email'}</strong>. Code expires in 15 minutes.
              </p>
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', color: 'var(--text-secondary)', fontWeight: '500' }}>Verification Code</label>
              <input type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={verifyCode} onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))} placeholder="123456" required data-testid="verify-code-input"
                style={{ width: '100%', padding: '14px', border: '1px solid var(--border-medium)', borderRadius: '8px', fontSize: '24px', outline: 'none', background: 'var(--bg-primary)', color: 'var(--text-primary)', textAlign: 'center', letterSpacing: '8px', fontFamily: 'monospace' }} autoFocus />
            </div>
            <button type="submit" disabled={loading} data-testid="verify-submit-btn"
              style={{ width: '100%', padding: '14px', background: '#00E5FF', color: '#000', border: 'none', borderRadius: '10px', fontSize: '1rem', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', opacity: loading ? 0.7 : 1, boxShadow: '0 0 12px #00E5FF', marginBottom: '12px' }}>
              {loading ? <Loader2 size={20} className="animate-spin" /> : <><ShieldCheck size={20} /> Verify Email</>}
            </button>
            <button type="button" onClick={handleResend} data-testid="verify-resend-btn"
              style={{ width: '100%', padding: '10px', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-medium)', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
              Resend code
            </button>
          </form>
        ) : (
        /* Login/Signup Form */
        <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
          {mode === 'signup' && (
            <>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', color: 'var(--text-secondary)', fontWeight: '500' }}>{t('auth.fullName')}</label>
                <div style={{ position: 'relative' }}>
                  <User size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-light)' }} />
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="John Smith" required data-testid="auth-name-input"
                    style={{ width: '100%', padding: '12px 12px 12px 42px', border: '1px solid var(--border-medium)', borderRadius: '8px', fontSize: '1rem', outline: 'none', background: 'var(--bg-primary)', color: 'var(--text-primary)' }} />
                </div>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', color: 'var(--text-secondary)', fontWeight: '500' }}>Phone Number</label>
                <div style={{ position: 'relative' }}>
                  <Phone size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-light)' }} />
                  <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123-4567" required data-testid="auth-phone-input"
                    style={{ width: '100%', padding: '12px 12px 12px 42px', border: '1px solid var(--border-medium)', borderRadius: '8px', fontSize: '1rem', outline: 'none', background: 'var(--bg-primary)', color: 'var(--text-primary)' }} />
                </div>
              </div>
            </>
          )}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', color: 'var(--text-secondary)', fontWeight: '500' }}>{t('auth.emailAddress')}</label>
            <div style={{ position: 'relative' }}>
              <Mail size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-light)' }} />
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required data-testid="auth-email-input"
                style={{ width: '100%', padding: '12px 12px 12px 42px', border: '1px solid var(--border-medium)', borderRadius: '8px', fontSize: '1rem', outline: 'none', background: 'var(--bg-primary)', color: 'var(--text-primary)' }} />
            </div>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', color: 'var(--text-secondary)', fontWeight: '500' }}>{t('auth.password')}</label>
            <div style={{ position: 'relative' }}>
              <Lock size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-light)' }} />
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required minLength={6} data-testid="auth-password-input"
                style={{ width: '100%', padding: '12px 12px 12px 42px', border: '1px solid var(--border-medium)', borderRadius: '8px', fontSize: '1rem', outline: 'none', background: 'var(--bg-primary)', color: 'var(--text-primary)' }} />
            </div>
          </div>
          {mode === 'login' && (
            <div style={{ marginBottom: '16px', textAlign: 'right' }}>
              <button
                type="button"
                onClick={() => { setForgotMode(true); setForgotEmail(email); }}
                data-testid="auth-forgot-password-link"
                style={{ background: 'none', border: 'none', color: '#00E5FF', fontSize: '0.85rem', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
              >
                Forgot password?
              </button>
            </div>
          )}
          <button type="submit" disabled={loading} data-testid="auth-submit-btn"
            style={{ width: '100%', padding: '14px', background: '#00E5FF', color: '#000', border: 'none', borderRadius: '10px', fontSize: '1rem', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', opacity: loading ? 0.7 : 1, boxShadow: '0 0 12px #00E5FF' }}>
            {loading ? <Loader2 size={20} className="animate-spin" /> : mode === 'login' ? <><LogIn size={20} />{t('auth.signInBtn')}</> : <><UserPlus size={20} />{t('auth.createAccountBtn')}</>}
          </button>
        </form>
        )}

        {/* Toggle Mode */}
        {!verifyMode && (
        <div style={{ padding: '0 24px 24px', textAlign: 'center' }}>
          <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
            {mode === 'login' ? t('auth.noAccount') + ' ' : t('auth.hasAccount') + ' '}
            <button type="button" onClick={() => setMode(mode === 'login' ? 'signup' : 'login')}
              style={{ background: 'none', border: 'none', color: '#00E5FF', fontWeight: '600', cursor: 'pointer', textDecoration: 'underline' }}>
              {mode === 'login' ? t('auth.signUpLink') : t('auth.signInLink')}
            </button>
          </p>
        </div>
        )}
        </>)}
      </div>
    </div>
  );
};

export default AuthModal;
